import type { ReactNode } from 'react';
import { Reveal } from '@/components/effects/index.js';

/**
 * Page header — display-font title + optional eyebrow / subtitle / right
 * action area. Comes pre-wrapped in Reveal so every page gets a polished
 * fade-up on first paint without each caller having to remember.
 */

interface Props {
  title: string;
  /** Small caps label above the title — categorical hint. e.g. "用户". */
  eyebrow?: string;
  subtitle?: ReactNode;
  right?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  eyebrow,
  subtitle,
  right,
  className = '',
}: Props): JSX.Element {
  return (
    <Reveal y={12} duration={0.5}>
      <div
        className={`flex items-start justify-between gap-3 flex-wrap mb-6 ${className}`}
      >
        <div className="min-w-0">
          {eyebrow !== undefined && (
            <div className="text-[11px] uppercase tracking-[0.18em] text-shark-300 font-semibold mb-1.5">
              {eyebrow}
            </div>
          )}
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle !== undefined && (
            <div className="text-sm text-slate-400 mt-2 max-w-prose">{subtitle}</div>
          )}
        </div>
        {right !== undefined && <div className="shrink-0">{right}</div>}
      </div>
    </Reveal>
  );
}
